import { omit, pickBy } from 'lodash';

import { IWorkOrder } from '../models';
import { initialWorkOrder } from './initialState';

/**
 * Pure normalization helpers for work orders coming back from the API.
 *
 * Kept free of the auth/MSAL import chain (unlike the reducer that consumes
 * them) so they can be unit tested directly.
 */

/**
 * Backend returns work order parts with material (Material), not part (Part).
 * Map material -> part for UI (EditWorkOrderForm expects item.part.number, item.part.description).
 */
export const normalizeWorkOrderParts = (parts: any[] | undefined): any[] => {
    if (!Array.isArray(parts)) return [];
    return parts.map((p: any) => {
        const material = p.material || p.Material;
        return {
            ...p,
            part: material
                ? {
                      id: material.id,
                      number: material.number ?? '',
                      description: material.description ?? material.name ?? '',
                      installationTime: material.installationTime ?? 0
                  }
                : undefined
        };
    });
};

export const cleanWorkOrderObject = (workOrder: any): IWorkOrder => {
    // todo remove facility
    const normalizedWorkOrder = omit(workOrder, 'jobWorkOrders');
    const assignedUser = workOrder.jobWorkOrders?.[0]?.job?.assignedUser;
    const parts = normalizeWorkOrderParts(normalizedWorkOrder.parts);
    // Backend now returns the work order's equipment as `material` (it was
    // `product` before the Materials refactor). Map it back to `product` so
    // consumers still keyed on `product` keep working — most importantly the
    // SAP job-type dropdown (getJobTypeOptionsForWorkOrder), which bails out
    // and returns no options when product is undefined.
    const product = normalizedWorkOrder.product ?? normalizedWorkOrder.material;
    return {
        ...initialWorkOrder,
        ...pickBy(normalizedWorkOrder, property => property !== null),
        parts,
        product,
        jobNumber: workOrder.jobWorkOrders?.[0]?.job?.jobNumber,
        assignedUser: assignedUser
            ? `${assignedUser.first} ${assignedUser.last}`
            : undefined
    };
};
